import React, { useContext, useEffect } from "react";
import { ActionIcon } from "rizzui/action-icon";
import { Text } from "rizzui/typography";
import { useModal } from "../hooks/useModal";
import Select from "react-select";
import { inputClassName, labelClassName } from "../modules/projects/new/first";
import { Controller, useForm } from "react-hook-form";
import { useColorStyle } from "../hooks/useColorStyle";
import { ThemeContext } from "../context/themeContext";
import CountryDropdown from "./countrySelect";
import { Radio } from "rizzui/radio";
import cn from "../lib/utils/class-name";
import { useBuyPhone } from "../hooks/useProfileDetails";
import { useNotifcication } from "../hooks/useNotification";
import { useQueryClient } from "@tanstack/react-query";
import { AuthContext } from "../context/authContext";
import { Button } from "rizzui/button";

export default function CreateNumber() {
  const { closeModal, view } = useModal();
  const { theme } = useContext(ThemeContext);
  const { token } = useContext(AuthContext);
  const { campaignColorStyles } = useColorStyle(theme);
  const { openNotification } = useNotifcication();
  const queryClient = useQueryClient();

  const {
    register,
    formState: { errors },
    handleSubmit,
    reset,
    getValues,
    setValue,
    watch,
    control,
  } = useForm({});

  useEffect(() => {
    reset();
  }, [view]);

  const { mutateAsync, isPending } = useBuyPhone(
    watch("country")?.code,
    watch("type")
  );

  const handleNumber = handleSubmit(async () => {
    try {
      const result = await mutateAsync();
      if (result) {
        queryClient.invalidateQueries({ queryKey: ["user_phones", token] });
        openNotification({
          status: "success",
          message: "Number created successfully",
        });
        closeModal();
      } else {
        openNotification({
          status: "error",
          message: "Error creating number",
        });
      }
    } catch (err) {
      openNotification({
        status: "error",
        message: err.message || "Error creating number",
      });
    }
  });

  return (
    <div className="w-[96%] md:w-[471px] mx-auto bg-background dark:bg-[#2A2C30] p-[32px] rounded-[8px] shadow-lg">
      <button
        className="absolute top-4 right-4 p-1 rounded-lg z-10 text-gray-400 bg-transparent hover:text-gray-600 dark:hover:text-gray-300"
        onClick={closeModal}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          fill="currentColor"
          className="size-[18px]"
        >
          <path
            fillRule="evenodd"
            d="M5.47 5.47a.75.75 0 0 1 1.06 0L12 10.94l5.47-5.47a.75.75 0 1 1 1.06 1.06L13.06 12l5.47 5.47a.75.75 0 1 1-1.06 1.06L12 13.06l-5.47 5.47a.75.75 0 0 1-1.06-1.06L10.94 12 5.47 6.53a.75.75 0 0 1 0-1.06Z"
            clipRule="evenodd"
          />
        </svg>
      </button>
      <div className="flex flex-col gap-[28px] ">
        <div className="flex flex-col gap-[8px]">
          <Text className="font-ibmPlexSerif font-[600] text-[18px] text-primary-dark">
            Create a new number
          </Text>
          <Text className="font-switzer text-[14px] text-[#758599] font-[400] max-w-[379px]">
            Select the country for your number, and we’ll automatically deduct
            the cost from your credit
          </Text>
        </div>
        <form
          className="flex flex-col gap-[28px] "
          noValidate
          onSubmit={(e) => {
            e.preventDefault();
            handleNumber();
          }}
        >
          <Controller
            control={control}
            name="country"
            rules={{
              required: {
                value: true,
                message: "Required",
              },
            }}
            render={({ field: { onChange, value, name } }) => (
              <div className="w-full">
                <label className={labelClassName}>Country</label>
                <CountryDropdown
                  onChange={(code) => {
                    onChange({ code }); // Pass an object with the code
                  }}
                  value={value?.code} // Pass only the code
                  name={name}
                  allowedCountries={["GB"]} // Example of allowed countries
                />
                {errors.country?.message && (
                  <p className="text-red text-[13px] mt-0.5 rizzui-textarea-error-text">
                    {"" + errors.country.message}
                  </p>
                )}
              </div>
            )}
          />
          {watch("country")?.code && (
            <div className="-mt-[16px]">
              <div className="flex flex-row gap-[16px]">
                <Radio
                  label="Local"
                  size="sm"
                  value="local"
                  labelClassName={cn(
                    labelClassName,
                    "!text-primary-dark !ml-[12px]"
                  )}
                  {...register("type", {
                    required: { value: true, message: "Select an option" },
                  })}
                />
                <Radio
                  label="Mobile"
                  labelClassName={cn(
                    labelClassName,
                    "!text-primary-dark !ml-[12px]"
                  )}
                  size="sm"
                  value="mobile"
                  {...register("type", {
                    required: { value: true, message: "Select an option" },
                  })}
                />
              </div>
              {errors?.type?.message && (
                <p className="text-red text-[12px] mt-1 rizzui-textarea-error-text">
                  {"" + errors.type.message}
                </p>
              )}
            </div>
          )}

          <Button
            className="w-full h-[40px] rounded-[4px] !bg-btn"
            type="submit"
            isLoading={isPending}
          >
            <Text className="font-[400] text-[12px] text-background font-switzer">
              Pay $5 to create a new number
            </Text>
          </Button>
        </form>
      </div>
    </div>
  );
}
